"use client";
import React, { useEffect } from "react";
import { site } from "../../config/site";

export default function ContactMain() {
  // Hide CTA/Newsletter blocks on this page
  useEffect(() => {
    const sel =
      ".cta-one, .newsletter-one, section[class*='subscribe'], section[class*='newsletter']";
    const prev = new Map();
    const hideAll = () => {
      document.querySelectorAll(sel).forEach((el) => {
        if (!prev.has(el)) prev.set(el, el.style.display);
        el.style.display = "none";
      });
    };
    hideAll();
    const obs = new MutationObserver(hideAll);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => {
      obs.disconnect();
      prev.forEach((v, el) => {
        if (document.body.contains(el)) el.style.display = v || "";
      });
    };
  }, []);

  const sentOk =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("sent") === "1";

  return (
    <>
      {/* Contact One Start */}
      <section className="contact-one">
        <div className="container">
          <div className="contact-one__inner">
            <h3 className="contact-one__title">{site.contactIntroTitle}</h3>
            <p className="contact-one__text">{site.contactIntroText}</p>

            {/* Success message after redirect */}
            {sentOk && (
              <div
                style={{
                  background: "rgba(40,167,69,.15)",
                  border: "1px solid rgba(40,167,69,.45)",
                  color: "#a3e2b4",
                  padding: "12px 16px",
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              >
                ✅ Your message was sent. Thank you!
              </div>
            )}

            {/* Send directly to email via FormSubmit */}
            <form
              className="contact-one__form"
              action={`https://formsubmit.co/${site.formSubmitEmail}`}
              method="POST"
            >
              {/* FormSubmit config */}
              <input
                type="hidden"
                name="_subject"
                value="NSM — New contact message"
              />
              <input type="hidden" name="_template" value="table" />
              <input type="hidden" name="_captcha" value="false" />
              {/* honeypot to reduce spam */}
              <input
                type="text"
                name="_honey"
                style={{ display: "none" }}
                tabIndex="-1"
                autoComplete="off"
              />
              {/* Redirect back to this page with success flag */}
              <input type="hidden" name="_next" value="/contact?sent=1" />

              <div className="row">
                <div className="col-xl-6 col-lg-6">
                  <div className="contact-one__input-box">
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      required
                    />
                  </div>
                </div>
                <div className="col-xl-6 col-lg-6">
                  <div className="contact-one__input-box">
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      required
                    />
                  </div>
                </div>
                <div className="col-xl-6 col-lg-6">
                  <div className="contact-one__input-box">
                    <input
                      type="text"
                      name="phone"
                      placeholder="Phone Number"
                    />
                  </div>
                </div>
                <div className="col-xl-6 col-lg-6">
                  <div className="contact-one__input-box">
                    <div className="select-box">
                      <select
                        className="selectmenu wide"
                        defaultValue="Choose Option"
                        name="reason"
                        aria-label="Reason"
                      >
                        <option value="Choose Option" disabled>
                          Choose an option
                        </option>
                        <option value="Model Application">Model Application</option>
                        <option value="Press & Media">Press & Media</option>
                        <option value="Sponsorship / Partner">
                          Sponsorship / Partner
                        </option>
                        <option value="VIP / Tickets">VIP / Tickets</option>
                        <option value="Vendor / Booth">Vendor / Booth</option>
                        <option value="Volunteer / Backstage">
                          Volunteer / Backstage
                        </option>
                        <option value="Collaboration / Booking">
                          Collaboration / Booking
                        </option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="col-xl-12">
                  <div className="contact-one__input-box text-message-box">
                    <textarea
                      name="message"
                      placeholder="Your Message"
                      rows={5}
                      required
                    />
                  </div>
                  <div className="contact-one__btn-box">
                    <button type="submit" className="thm-btn contact-one__btn">
                      Submit Now <span className="icon-arrow-right"></span>
                    </button>
                  </div>
                  <p style={{ opacity: 0.7, fontSize: 14, marginTop: 12 }}>
                    We care about your privacy. We only use your details to
                    respond to your inquiry and do not sell your data.
                  </p>
                </div>
              </div>
            </form>
            <div className="result" />
          </div>
        </div>
      </section>
      {/* Contact One End */}

      {/* Contact Two Start */}
      <section className="contact-two">
        <div className="container">
          <div className="row">
            {/* Location */}
            <div className="col-xl-4 col-lg-4">
              <div className="contact-two__single">
                <div className="contact-two__icon">
                  <span className="icon-pin"></span>
                </div>
                <h3 className="contact-two__title">Location</h3>
                <p className="contact-two__text">
                  {site.companyAddressLines.map((l, i) => (
                    <React.Fragment key={i}>
                      {l}
                      {i < site.companyAddressLines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            </div>

            {/* E-mail */}
            <div className="col-xl-4 col-lg-4">
              <div className="contact-two__single">
                <div className="contact-two__icon">
                  <span className="icon-paper-plan"></span>
                </div>
                <h3 className="contact-two__title">E-mail</h3>
                <p className="contact-two__text">
                  <a href={`mailto:${site.contactEmail}`}>{site.contactEmail}</a>
                </p>
              </div>
            </div>

            {/* Contact (Phone) */}
            <div className="col-xl-4 col-lg-4">
              <div className="contact-two__single">
                <div className="contact-two__icon">
                  <span className="icon-call"></span>
                </div>
                <h3 className="contact-two__title">Contact</h3>
                <p className="contact-two__text">
                  <a href={`tel:${site.contactPhoneTel}`}>{site.contactPhone}</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Contact Two End */}
    </>
  );
}
